import axios from 'axios';
import { API_CONFIG, STORAGE_KEYS } from '../../constants';
import {
  IContact,
  ContactFilters,
  CreateContactRequest,
  GetContactsResponse,
  UpdateContactRequest,
  BulkCreateContactsRequest,
  BulkCreateContactsResponse,
  ContactSegmentsFilters,
  GetContactSegmentsResponse,
} from '../../types/contacts/contacts.types';
import { EmailSentHistoryFilters } from '../../types/emailSentHistory/emailSentHistory.types';
import { GetContactEmailsResponse } from '../../types/contacts/contacts.types';

const SERVER_URL = API_CONFIG.SERVER_URL;

// Create axios instance with default config
const api = axios.create({
  baseURL: SERVER_URL,
});

// Add request interceptor to include token in headers
api.interceptors.request.use(
  (config) => {
    const token = localStorage.getItem(STORAGE_KEYS.TOKEN);
    if (token && config.headers) {
      config.headers.Authorization = `Bearer ${token}`;
    }
    return config;
  },
  (error) => {
    return Promise.reject(error);
  }
);

// Add response interceptor to handle token expiration
api.interceptors.response.use(
  (response) => response,
  (error) => {
    if (error.response?.status === 401) {
      // Token expired or invalid, redirect to login
      localStorage.removeItem(STORAGE_KEYS.TOKEN);
      localStorage.removeItem(STORAGE_KEYS.USER);
      window.location.href = '/';
    }
    return Promise.reject(error);
  }
);

// Get all contacts with pagination and filters
export const getContacts = async (
  filters: ContactFilters = {}
): Promise<GetContactsResponse> => {
  try {
    const params = new URLSearchParams();

    if (filters.page !== undefined)
      params.append('page', filters.page.toString());
    if (filters.limit !== undefined)
      params.append('limit', filters.limit.toString());
    if (filters.search) params.append('search', filters.search);
    if (filters.sortBy) params.append('sortBy', filters.sortBy);
    if (filters.sortOrder) params.append('sortOrder', filters.sortOrder);

    if (filters.filterBy) params.append('filterBy', filters.filterBy);
    if (filters.filterType) params.append('filterType', filters.filterType);
    if (filters.filterValue && Object.keys(filters.filterValue).length > 0) {
      params.append('filterValue', JSON.stringify(filters.filterValue));
    }

    const response = await api.get(`/contacts/?${params}`);
    return response.data;
  } catch (error) {
    console.error('Error fetching contacts:', error);
    throw error;
  }
};

// Get contact by ID
export const getContactById = async (id: string): Promise<IContact> => {
  try {
    const response = await api.get(`/contacts/${id}`);
    return response.data.contact;
  } catch (error) {
    console.error('Error fetching contact:', error);
    throw error;
  }
};

// Create or update contact
export const createContact = async (
  contactData: CreateContactRequest
): Promise<IContact> => {
  try {
    const response = await api.post('/contacts/', contactData);
    return response.data;
  } catch (error) {
    console.error('Error creating contact:', error);
    throw error;
  }
};

// Update contact
export const updateContact = async (
  contactData: UpdateContactRequest
): Promise<IContact> => {
  try {
    const response = await api.patch(
      `/contacts/${contactData.id}`,
      contactData.data
    );
    return response.data;
  } catch (error) {
    console.error('Error updating contact:', error);
    throw error;
  }
};
// Delete contact
export const deleteContact = async (id: string): Promise<void> => {
  try {
    await api.delete(`/contacts/${id}`);
  } catch (error) {
    console.error('Error deleting contact:', error);
    throw error;
  }
};

// Bulk create contacts
export const bulkCreateContacts = async (
  data: BulkCreateContactsRequest
): Promise<BulkCreateContactsResponse> => {
  try {
    const response = await api.post('/contacts/upload/csv', data);
    return response.data;
  } catch (error) {
    console.error('Error bulk creating contacts:', error);
    throw error;
  }
};

// Bulk delete contacts
export const bulkDeleteContacts = async (ids: string[]): Promise<void> => {
  try {
    await api.delete('/contacts/bulk', {
      data: { ids },
    });
  } catch (error) {
    console.error('Error bulk deleting contacts:', error);
    throw error;
  }
};

export const getContactFields = async (): Promise<any> => {
  try {
    const response = await api.get('/contacts/fields');

    return response.data.data.fieldNames;
  } catch (error) {
    console.error('Error fetching contact fields:', error);
    throw error;
  }
};

// Export contacts to CSV
export const exportContactsCSV = async (search?: string): Promise<Blob> => {
  try {
    const params = new URLSearchParams();
    if (search) params.append('search', search);

    const response = await api.get(`/contacts/csv?${params}`, {
      responseType: 'blob',
    });
    return response.data;
  } catch (error) {
    console.error('Error exporting contacts:', error);
    throw error;
  }
};

// Delete all contacts
export const deleteAllContacts = async (): Promise<void> => {
  try {
    await api.delete('/contacts/all');
  } catch (error) {
    console.error('Error deleting all contacts:', error);
    throw error;
  }
};

// Get contact segments
export const getContactSegments = async (
  contactId: string,
  filters: ContactSegmentsFilters = {}
): Promise<GetContactSegmentsResponse> => {
  try {
    const params = new URLSearchParams();

    if (filters.page !== undefined)
      params.append('page', filters.page.toString());
    if (filters.limit !== undefined)
      params.append('limit', filters.limit.toString());
    if (filters.search) params.append('search', filters.search);
    if (filters.sortBy) params.append('sortBy', filters.sortBy);
    if (filters.sortOrder) params.append('sortOrder', filters.sortOrder);
    if (filters.type) params.append('type', filters.type);

    const response = await api.get(`/contacts/${contactId}/segments?${params}`);
    return response.data;
  } catch (error) {
    console.error('Error fetching contact segments:', error);
    throw error;
  }
};

// Get contact emails
export const getContactEmails = async (
  contactId: string,
  filters: EmailSentHistoryFilters = {}
): Promise<GetContactEmailsResponse> => {
  try {
    const params = new URLSearchParams();

    if (filters.page !== undefined)
      params.append('page', filters.page.toString());
    if (filters.limit !== undefined)
      params.append('limit', filters.limit.toString());
    if (filters.status) params.append('status', filters.status);

    const response = await api.get(`/contacts/${contactId}/emails?${params}`);
    return response.data;
  } catch (error) {
    console.error('Error fetching contact emails:', error);
    throw error;
  }
};

export default api;
